import {
  BellIcon,
  CheckIcon,
  ChevronDownIcon,
  EllipsisVerticalIcon,
  LaptopIcon,
  SettingsIcon,
  XIcon,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { revokeAuthSession } from "@/lib/auth/session-api";
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type Notification,
} from "@/lib/notifications/notification-api";
import { cn } from "@/lib/utils";

const NOTIFICATION_TABS = [
  { key: "all", labelKey: "common:notifications.tabs.all" },
  { key: "system", labelKey: "common:notifications.tabs.system" },
] as const;

type NotificationTab = (typeof NOTIFICATION_TABS)[number]["key"];

export function NotificationPopover() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<NotificationTab>("all");
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const close = (event: MouseEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent && event.key !== "Escape") return;
      if (
        event instanceof MouseEvent &&
        popoverRef.current?.contains(event.target as Node)
      )
        return;
      setIsOpen(false);
      setIsActionsOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [isOpen]);

  const unreadCount = notifications.filter(
    (notification) => notification.readAt === null,
  ).length;
  const filteredNotifications = useMemo(
    () =>
      notifications.filter(
        (notification) => activeTab === "all" || notification.category === activeTab,
      ),
    [activeTab, notifications],
  );

  async function handleMarkAsRead(notification: Notification) {
    await markNotificationRead(notification.id);
    setNotifications((current) =>
      current.map((item) =>
        item.id === notification.id
          ? { ...item, readAt: new Date().toISOString() }
          : item,
      ),
    );
  }

  async function handleAction(notification: Notification) {
    const sessionId = notification.actionPayload?.sessionId;
    if (notification.actionType !== "revoke_session" || typeof sessionId !== "string")
      return;
    await revokeAuthSession(sessionId);
    await handleMarkAsRead(notification);
    setNotifications((current) =>
      current.map((item) =>
        item.id === notification.id
          ? { ...item, actionType: null, actionLabel: null, actionPayload: null }
          : item,
      ),
    );
  }

  async function handleMarkAllAsRead() {
    await markAllNotificationsRead();
    const readAt = new Date().toISOString();
    setNotifications((current) => current.map((item) => ({ ...item, readAt })));
    setIsActionsOpen(false);
  }

  async function loadNotifications() {
    setIsLoading(true);
    setLoadError(false);
    try {
      setNotifications(await fetchNotifications());
    } catch {
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  }

  function togglePopover() {
    const opening = !isOpen;
    setIsOpen(opening);
    setIsActionsOpen(false);
    if (opening) void loadNotifications();
  }

  return (
    <div ref={popoverRef} className="relative">
      <button
        type="button"
        aria-label={t("common:nav.openNotifications")}
        aria-expanded={isOpen}
        className="relative flex h-7 w-7 items-center justify-center rounded-full text-nav-text transition-colors hover:bg-nav-item-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={togglePopover}
      >
        <BellIcon aria-hidden="true" className="h-5 w-5" strokeWidth={1.75} />
        {unreadCount > 0 ? (
          <span className="absolute -right-1 -top-1 flex h-notification-badge min-w-notification-badge items-center justify-center rounded-full border-2 border-bg-elevated bg-status-block px-1 text-xs font-medium text-text-inverse">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        ) : null}
      </button>
      {isOpen ? (
        <NotificationPanel
          activeTab={activeTab}
          notifications={filteredNotifications}
          isActionsOpen={isActionsOpen}
          isLoading={isLoading}
          loadError={loadError}
          onChangeTab={setActiveTab}
          onToggleActions={() => setIsActionsOpen((open) => !open)}
          onMarkAllAsRead={() => void handleMarkAllAsRead()}
          onMarkAsRead={(notification) => void handleMarkAsRead(notification)}
          onAction={(notification) => void handleAction(notification)}
          onClose={() => setIsOpen(false)}
          onCloseActions={() => setIsActionsOpen(false)}
        />
      ) : null}
    </div>
  );
}

interface NotificationPanelProps {
  activeTab: NotificationTab;
  notifications: Notification[];
  isActionsOpen: boolean;
  isLoading: boolean;
  loadError: boolean;
  onChangeTab: (tab: NotificationTab) => void;
  onToggleActions: () => void;
  onMarkAllAsRead: () => void;
  onMarkAsRead: (notification: Notification) => void;
  onAction: (notification: Notification) => void;
  onClose: () => void;
  onCloseActions: () => void;
}

function NotificationPanel({
  activeTab,
  notifications,
  isActionsOpen,
  isLoading,
  loadError,
  onChangeTab,
  onToggleActions,
  onMarkAllAsRead,
  onMarkAsRead,
  onAction,
  onClose,
  onCloseActions,
}: NotificationPanelProps) {
  const { t } = useTranslation();

  return (
    <div
      role="dialog"
      aria-label={t("common:notifications.title")}
      className="absolute right-0 top-full z-50 mt-2 w-notification-panel-mobile overflow-hidden rounded-md border border-border bg-bg-elevated shadow-lg sm:w-notification-panel-tablet md:w-notification-panel"
    >
      <div className="py-2 sm:py-3">
        <NotificationHeader
          isActionsOpen={isActionsOpen}
          onToggleActions={onToggleActions}
          onMarkAllAsRead={onMarkAllAsRead}
          onClose={onClose}
          onCloseActions={onCloseActions}
        />
        <div className="mt-2 h-px bg-border sm:mt-3" />
        <NotificationTabs activeTab={activeTab} onChange={onChangeTab} />
        <NotificationContent
          notifications={notifications}
          isLoading={isLoading}
          loadError={loadError}
          onMarkAsRead={onMarkAsRead}
          onAction={onAction}
        />
      </div>
    </div>
  );
}

interface NotificationHeaderProps {
  isActionsOpen: boolean;
  onToggleActions: () => void;
  onMarkAllAsRead: () => void;
  onClose: () => void;
  onCloseActions: () => void;
}

function NotificationHeader({
  isActionsOpen,
  onToggleActions,
  onMarkAllAsRead,
  onClose,
  onCloseActions,
}: NotificationHeaderProps) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between px-3 sm:mt-1 sm:px-4">
      <h2 className="text-lg font-semibold text-text-primary sm:text-xl">
        {t("common:notifications.title")}
      </h2>
      <div className="flex items-center gap-2 sm:gap-3">
        <div className="relative">
          <button
            type="button"
            aria-label={t("common:notifications.moreActions")}
            aria-expanded={isActionsOpen}
            className="flex h-5 w-5 items-center justify-center rounded-sm text-text-primary hover:bg-bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={onToggleActions}
          >
            <EllipsisVerticalIcon aria-hidden="true" className="h-5 w-5" />
          </button>
          {isActionsOpen ? (
            <div className="absolute right-0 top-full z-10 mt-1 w-48 rounded-md border border-border bg-bg-elevated p-1 shadow-md">
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm text-text-secondary hover:bg-bg-surface hover:text-text-primary"
                onClick={onMarkAllAsRead}
              >
                <CheckIcon aria-hidden="true" className="h-4 w-4" />
                {t("common:notifications.markAllAsRead")}
              </button>
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm text-text-secondary hover:bg-bg-surface hover:text-text-primary"
                onClick={onCloseActions}
              >
                <SettingsIcon aria-hidden="true" className="h-4 w-4" />
                {t("common:notifications.settings")}
              </button>
            </div>
          ) : null}
        </div>
        <button
          type="button"
          aria-label={t("common:notifications.close")}
          className="flex h-5 w-5 items-center justify-center rounded-sm text-text-primary hover:bg-bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={onClose}
        >
          <XIcon aria-hidden="true" className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

interface NotificationTabsProps {
  activeTab: NotificationTab;
  onChange: (tab: NotificationTab) => void;
}

function NotificationTabs({ activeTab, onChange }: NotificationTabsProps) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-start gap-2 overflow-x-auto px-3 py-2 sm:gap-3 sm:px-4">
      {NOTIFICATION_TABS.map((tab) => (
        <button
          key={tab.key}
          type="button"
          className={cn(
            "flex h-7 min-w-fit items-center justify-center whitespace-nowrap rounded-lg px-4 text-xs font-medium sm:h-8 sm:px-8 sm:text-sm",
            activeTab === tab.key
              ? "bg-nav-text text-text-inverse"
              : "bg-border text-text-secondary hover:bg-nav-border",
          )}
          onClick={() => onChange(tab.key)}
        >
          {t(tab.labelKey)}
        </button>
      ))}
    </div>
  );
}

interface NotificationContentProps {
  notifications: Notification[];
  isLoading: boolean;
  loadError: boolean;
  onMarkAsRead: (notification: Notification) => void;
  onAction: (notification: Notification) => void;
}

function NotificationContent({
  notifications,
  isLoading,
  loadError,
  onMarkAsRead,
  onAction,
}: NotificationContentProps) {
  const { t } = useTranslation();
  if (isLoading) {
    return (
      <div className="px-3 py-12 text-center text-sm text-text-muted sm:px-5">
        {t("common:notifications.loading")}
      </div>
    );
  }
  if (loadError) {
    return (
      <div
        role="alert"
        className="px-3 py-12 text-center text-sm text-action-danger sm:px-5"
      >
        {t("common:notifications.error")}
      </div>
    );
  }
  return (
    <div className="py-4 sm:py-7">
      <div className="max-h-notification-content overflow-y-auto">
        {notifications.length > 0 ? (
          notifications.map((notification, index) => (
            <NotificationRow
              key={notification.id}
              notification={notification}
              isLast={index === notifications.length - 1}
              onMarkAsRead={onMarkAsRead}
              onAction={onAction}
            />
          ))
        ) : (
          <div className="flex flex-col items-center justify-center px-4 py-8 text-center">
            <div className="mb-3 rounded-full bg-bg-surface p-4">
              <BellIcon aria-hidden="true" className="h-6 w-6 text-text-muted" />
            </div>
            <p className="mb-1 text-text-muted">{t("common:notifications.emptyTitle")}</p>
            <p className="text-sm text-text-muted">
              {t("common:notifications.emptyDescription")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

interface NotificationRowProps {
  notification: Notification;
  isLast: boolean;
  onMarkAsRead: (notification: Notification) => void;
  onAction: (notification: Notification) => void;
}

function NotificationRow({
  notification,
  isLast,
  onMarkAsRead,
  onAction,
}: NotificationRowProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const isUnread = notification.readAt === null;

  return (
    <div
      className={cn(
        "transition-colors hover:bg-bg-surface",
        isUnread && "border-l-4 border-brand-blue-500",
      )}
    >
      <div className="flex gap-2 px-3 py-3 sm:px-5">
        <NotificationTypeIcon eventType={notification.eventType} isUnread={isUnread} />
        <div
          className={cn(
            "flex min-w-0 flex-1 flex-col justify-start gap-1 text-sm",
            isUnread ? "font-medium text-text-primary" : "text-text-secondary",
          )}
        >
          <p className="m-0 text-sm sm:text-base">{notification.message}</p>
          <p className="m-0 text-xs text-text-muted sm:text-sm">
            {new Date(notification.createdAt).toLocaleString()}
          </p>
          {notification.actionType && notification.actionLabel ? (
            <button
              type="button"
              className="mt-1 w-fit text-xs text-brand-blue-500 hover:underline"
              onClick={() => onAction(notification)}
            >
              {notification.actionLabel}
            </button>
          ) : null}
          <button
            type="button"
            className="mt-1 flex w-fit items-center gap-1 text-xs text-text-muted hover:text-text-secondary"
            onClick={() => setIsExpanded((expanded) => !expanded)}
          >
            <ChevronDownIcon
              aria-hidden="true"
              className={cn(
                "h-3.5 w-3.5 transition-transform",
                isExpanded && "rotate-180",
              )}
            />
            {t(isExpanded ? "common:notifications.less" : "common:notifications.details")}
          </button>
          {isExpanded ? (
            <div className="mt-2 rounded-md bg-bg-surface p-2 text-xs text-text-secondary">
              <p className="m-0">{notification.title}</p>
            </div>
          ) : null}
        </div>
        {isUnread ? (
          <button
            type="button"
            className="shrink-0 text-xs text-brand-blue-500 hover:underline"
            onClick={() => onMarkAsRead(notification)}
          >
            {t("common:notifications.markAsRead")}
          </button>
        ) : null}
      </div>
      {!isLast ? (
        <div className="px-3 sm:px-5">
          <div className="h-px bg-border opacity-50" />
        </div>
      ) : null}
    </div>
  );
}

function NotificationTypeIcon({
  eventType,
  isUnread,
}: {
  eventType: string;
  isUnread: boolean;
}) {
  const className = cn(
    "mt-1 h-5 w-5 shrink-0",
    isUnread ? "text-brand-blue-500" : "text-text-muted",
  );
  return eventType === "new_device_login" ? (
    <LaptopIcon aria-hidden="true" className={className} strokeWidth={1.75} />
  ) : (
    <SettingsIcon aria-hidden="true" className={className} strokeWidth={1.75} />
  );
}
